/**
 * Per-environment Cognito configuration. APP_ENV selects the pool at build time.
 * redirectUri/logoutRedirectUri default to the runtime origin. issuer/jwksUri are
 * optional; when omitted they are resolved via OIDC discovery.
 */

// In-app callback route after login / logout.
const REDIRECT_PATH = '/authenticated';
const LOGOUT_REDIRECT_PATH = '/logout';

// Confirmed DEV/SIT Cognito parameters.
const DEV_DOMAIN = 'https://auth.inspb2b-dev.insurance.hsbc.com.sg';
const DEV_CLIENT_ID = '1omrlu17uvhmvjr1nni58r3chc';
const DEFAULT_SCOPE = 'openid email phone';

// OIDC issuer and JWKS are served from the Cognito regional endpoint, not the custom Hosted UI domain.
const DEV_ISSUER = 'https://cognito-idp.ap-east-1.amazonaws.com/ap-east-1_36o4nzGMN';
const DEV_JWKS_URI = `${DEV_ISSUER}/.well-known/jwks.json`;

// Confirmed UAT Cognito parameters (ePortal-uat-public-client).
const UAT_DOMAIN = 'https://auth.inspb2b-uat.insurance.hsbc.com.sg';
const UAT_CLIENT_ID = '1ivsk5l2k4jpsnbehkdapg184i';
const UAT_ISSUER = 'https://cognito-idp.ap-east-1.amazonaws.com/ap-east-1_IUU9h5HPZ';
const UAT_JWKS_URI = `${UAT_ISSUER}/.well-known/jwks.json`;

// Confirmed PROD Cognito parameters (PKCE ePortal client). Registered callback URLs are
// https://eportal.life.hsbc.com.sg/authenticated and .../logout, which match the
// REDIRECT_PATH / LOGOUT_REDIRECT_PATH derived from the runtime origin below.
const PROD_DOMAIN = 'https://auth.inspb2b.insurance.hsbc.com.sg';
const PROD_CLIENT_ID = '2ciobog8uqhpf8g7b7ld9uc2bi';
const PROD_ISSUER = 'https://cognito-idp.ap-east-1.amazonaws.com/ap-east-1_n3x6LqcY3';
const PROD_JWKS_URI = `${PROD_ISSUER}/.well-known/jwks.json`;

// TEMPORARY: SIT builds use the UAT Cognito pool. Keep in sync with src/server/cid/config.js.
const SIT_COGNITO_POOL_OVERRIDE = 'UAT';

// Only SIT / UAT / PROD are configured; any other APP_ENV falls back to SIT.
const COGNITO_CONFIG = {
  SIT: {
    // Original SIT config (DEV pool); preserved for rollback when the override is removed.
    domain: DEV_DOMAIN,
    clientId: DEV_CLIENT_ID,
    scope: DEFAULT_SCOPE,
    issuer: DEV_ISSUER,
    jwksUri: DEV_JWKS_URI,
    redirectUri: null,
    logoutRedirectUri: null,
  },
  UAT: {
    domain: UAT_DOMAIN,
    clientId: UAT_CLIENT_ID,
    scope: DEFAULT_SCOPE,
    issuer: UAT_ISSUER,
    jwksUri: UAT_JWKS_URI,
    redirectUri: null,
    logoutRedirectUri: null,
  },
  PROD: {
    domain: PROD_DOMAIN,
    clientId: PROD_CLIENT_ID,
    scope: DEFAULT_SCOPE,
    issuer: PROD_ISSUER,
    jwksUri: PROD_JWKS_URI,
    redirectUri: null,
    logoutRedirectUri: null,
  },
};

const getOrigin = () =>
  (typeof window !== 'undefined' && window.location && window.location.origin) || '';

/** Return the Cognito configuration for the current environment with derived redirect URIs. */
export const getCognitoConfig = () => {
  const env = (typeof APP_ENV !== 'undefined' && APP_ENV) || 'SIT';
  // SIT resolves to the UAT pool while the override is set, without touching the SIT entry.
  const poolEnv = env === 'SIT' && SIT_COGNITO_POOL_OVERRIDE ? SIT_COGNITO_POOL_OVERRIDE : env;
  const cfg = COGNITO_CONFIG[poolEnv] || COGNITO_CONFIG.SIT;

  return {
    ...cfg,
    redirectUri: cfg.redirectUri || `${getOrigin()}${REDIRECT_PATH}`,
    logoutRedirectUri: cfg.logoutRedirectUri || `${getOrigin()}${LOGOUT_REDIRECT_PATH}`,
    issuer: cfg.issuer || null,
    jwksUri: cfg.jwksUri || null,
  };
};

export default getCognitoConfig;
